import React, { useEffect, useState } from 'react';
/**
 * `CreatePlanPage` renders the dealer journey plan configuration step.
 *
 * Responsibilities:
 * - Shows `ProductSelection` forms for the currently selected products.
 * - Applies default selections (tyre type/claim limit/cover level/payment
 *   method/duration) when only one option exists.
 * - Coordinates validation across products for checkout and save-quote flows.
 * - Supports saving quotes via `SaveQuoteConfirmation`.
 * - Prompts for missing VRN via `VrnInputModal` when quotes are enabled.
 * - Applies warranty policy start date defaulting based on manufacturer
 *   warranty duration and first registration date.
 */
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import ProductSelection, {
  ProductFormStatus
} from '../common/Product/ProductSelection';
import { ProductType, SelectedLinkedProduct } from '../common/CommonTypes';
import PriceTotal from './PriceTotal';
import './CreatePlanPage.scss';
import {
  PageAbsolutePath,
  PageRelativePath,
  ProductChannel,
  ProductTypes,
  QuoteType
} from '../../consts/Common';
import {
  getDefaultClaimLimit,
  getDefaultCoverLevel,
  getDefaultPaymentMethod,
  getDefaultDuration,
  getDefaultVariant,
  getSelectedProducts,
  getSelectedVariant,
  getUniqueCoverLevels,
  getUniqueTyreTypes
} from '../common/ProductMethods';
import PageHeader from '../common/PageHeader';
import { ButtonContinue } from '../common/Button/ButtonContinue';
import SaveQuoteConfirmation from '../common/SaveQuoteConfirmation';
import { ButtonBack } from '../common/Button/ButtonBack';
import { useJourney } from './Layout';
import VrnInputModal from '../common/VrnInputModal';
import { CommonValidation } from '../common/CommonValidation/commonValidation';
import { useTranslation } from 'react-i18next';
import {
  getJsonDateString,
  getSelectedNoOfInstallments
} from '../common/HelperMethods';
import { getDateByBrowserLanguage } from '../common/Formatter';
import { useAppSelector } from '../../app-state/Hooks';
import { useCurrencySymbol } from '../../hooks/useCurrencySymbol';

const CreatePlanPage = (): JSX.Element => {
  const navigate = useNavigate();
  const [priceInFocus, setPriceInFocus] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showVrnModal, setShowVrnModal] = useState<boolean>(false);
  const [disableSaveQuote, setDisableSaveQuote] = useState(false);
  const [validationResults, setValidationResults] = useState<{
    [key: number]: boolean;
  }>({});
  const [status, setStatus] = useState<ProductFormStatus>(
    ProductFormStatus.Idle
  );

  const {
    journey,
    onUpdateProducts,
    onPageChange,
    setPersonalDetails,
    onUpdateVehicleVrn
  } = useJourney();

  const appConfig = useAppSelector((s) => s.appConfig);
  const productVariantPage = useAppSelector(
    (s) => s.content.productVariantPage
  );

  const currencySymbol = useCurrencySymbol();

  const cms = productVariantPage;
  const {
    products,
    vehicle,
    quoteId,
    quoteProducts,
    quotedByDealerReference,
    manufacturingCoverageMonths
  } = journey;
  const { vehicleDeliveryDate, vehicleDetails } = vehicle;
  const { vrn, registrationDate } = vehicleDetails;
  const { t } = useTranslation();

  const selectedProducts = getSelectedProducts(products);

  const getMaxPolicyStartDateOffset = (product: ProductType) => {
    return product.productTypeId === ProductTypes.ServicePlan
      ? product.policyStartDateLimit
      : undefined;
  };

  const saveQuoteButtonText = t('customer-button.button-save-quote');
  const saveChangesButtonText = t('customer-button.button-save-changes-text');

  const noOfInstallments = Math.max(
    ...selectedProducts
      .map((p) => p.productVariants)
      .reduce((a, b) => a.concat(b))
      .map((v) => v.noOfInstallments),
    0
  );

  const [selectedLinkedProducts, setSelectedLinkedProducts] = useState<
    SelectedLinkedProduct[]
  >([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setProductDefaultSelection();

    if (products && products.length > 0) {
      const slp: SelectedLinkedProduct[] = products
        .map((q) => q.selectedLinkedProducts || [])
        .reduce((a, b) => a.concat(b), []);

      setSelectedLinkedProducts(slp);
    }
  }, []);

  useEffect(() => {
    setSaveQuoteButtonState();
  }, [products, status, quoteProducts]);

  useEffect(() => {
    const notAllProductsHaveBeenValidated = selectedProducts.some(
      (p) => validationResults[p.productTypeId] === undefined
    );

    if (notAllProductsHaveBeenValidated) {
      return;
    }

    const allValid = selectedProducts.every(
      (p) => validationResults[p.productTypeId]
    );

    if (!allValid) {
      setStatus(ProductFormStatus.Idle);
      return;
    }

    switch (status) {
      case ProductFormStatus.ValidatingCheckout:
        if (appConfig.includeQuotes && CommonValidation.isEmpty(vrn)) {
          setShowVrnModal(true);
        } else {
          navigate(PageRelativePath.PersonalDetails);
          onPageChange(PageAbsolutePath.Products, true);
        }
        break;
      case ProductFormStatus.ValidatingSaveQuote:
        setShowModal(true);
    }
  }, [validationResults]);

  const setSaveQuoteButtonState = () => {
    const isQuoteSame = selectedProducts.every((p) => {
      if (p.isPurchased) return true;

      const selectedVariant = getSelectedVariant(p.productVariants);

      if (!selectedVariant) return true;

      const quotedProduct = quoteProducts.find(
        (qp) => qp.productTypeId === p.productTypeId
      );

      if (!quotedProduct) return false;

      return (
        quotedProduct.variantId === selectedVariant.variantId &&
        quotedProduct.quotedPrice === selectedVariant.price
      );
    });

    const isDealerSame = quoteId
      ? vehicle.dealerReference === quotedByDealerReference
      : true;

    setDisableSaveQuote(
      (isQuoteSame || status !== ProductFormStatus.Idle) && isDealerSame
    );
  };

  const setProductDefaultSelection = () => {
    let updatedProducts: ProductType[] = products;

    selectedProducts.forEach((p) => {
      const selectedVariant = getSelectedVariant(p.productVariants);

      if (!selectedVariant) {
        const tyreTypes = getUniqueTyreTypes(p.productVariants);

        if (tyreTypes.length === 1) {
          const defaultTyreTypeId = tyreTypes[0];
          const defaultClaimLimit = getDefaultClaimLimit(
            p.productVariants,
            defaultTyreTypeId,
            currencySymbol
          );
          const defaultPaymentMethod = getDefaultPaymentMethod(
            p.productVariants,
            defaultTyreTypeId,
            defaultClaimLimit ? defaultClaimLimit.value : undefined,
            p.selectedCoverLevelId
          );
          const defaultDuration = getDefaultDuration(
            p.productVariants,
            defaultTyreTypeId,
            defaultClaimLimit ? defaultClaimLimit.value : undefined,
            p.selectedCoverLevelId,
            defaultPaymentMethod
          );
          const defaultVariant = getDefaultVariant(
            p.productVariants,
            defaultTyreTypeId,
            defaultClaimLimit ? defaultClaimLimit.value : undefined,
            p.selectedCoverLevelId,
            defaultPaymentMethod,
            defaultDuration,
            undefined
          );
          updatedProducts = getUpdatedProducts(
            updatedProducts,
            p.productTypeId,
            defaultTyreTypeId,
            defaultClaimLimit ? defaultClaimLimit.value : undefined,
            p.selectedCoverLevelId,
            p.selectedExcess,
            defaultPaymentMethod,
            defaultVariant?.variantId || ''
          );
        } else {
          const coverLevels = getUniqueCoverLevels(p.productVariants);

          if (coverLevels.length === 1) {
            const defaultCoverLevel = getDefaultCoverLevel(p.productVariants);
            const defaultPaymentMethod = getDefaultPaymentMethod(
              p.productVariants,
              p.selectedTyreTypeId,
              p.selectedClaimLimit,
              defaultCoverLevel?.value
            );
            const defaultDuration = getDefaultDuration(
              p.productVariants,
              p.selectedTyreTypeId,
              p.selectedClaimLimit,
              defaultCoverLevel?.value,
              defaultPaymentMethod
            );
            const defaultVariant = getDefaultVariant(
              p.productVariants,
              p.selectedTyreTypeId,
              p.selectedClaimLimit,
              defaultCoverLevel?.value,
              defaultPaymentMethod,
              defaultDuration,
              undefined
            );
            updatedProducts = getUpdatedProducts(
              updatedProducts,
              p.productTypeId,
              p.selectedTyreTypeId,
              p.selectedClaimLimit,
              defaultCoverLevel?.value,
              p.selectedExcess,
              defaultPaymentMethod,
              defaultVariant?.variantId || ''
            );
          }
        }
      } else if (
        (p.productTypeId === ProductTypes.Tyre && !p.selectedTyreTypeId) ||
        (p.productTypeId !== ProductTypes.Tyre && !p.selectedCoverLevelId)
      ) {
        updatedProducts = getUpdatedProducts(
          updatedProducts,
          p.productTypeId,
          selectedVariant.tyreTypeId,
          selectedVariant.claimLimit,
          selectedVariant.coverLevelId,
          selectedVariant.excess,
          selectedVariant.paymentMethod,
          selectedVariant.variantId
        );
      }
    });

    updatedProducts.forEach((p) => {
      p.selectedCoverLevelId =
        p.productTypeId === ProductTypes.Tyre
          ? undefined
          : p.selectedCoverLevelId;

      if (p.productTypeId === ProductTypes.Warranty) {
        const defaultStartDate = getDefaultStartDate();

        if (defaultStartDate) {
          p.policyStartDate = defaultStartDate;
        }
      }
    });

    onUpdateProducts(updatedProducts);
  };

  const addOrRemoveProduct = (
    productTypeCode: string,
    productTypeId: number
  ) => {
    const quotedProduct = quoteProducts.find(
      (qp) => qp.productTypeId === productTypeId
    );
    const quotedVariantId = quotedProduct?.variantId ?? '';

    const updatedProducts = products.map((product) => {
      if (product.productTypeCode !== productTypeCode) {
        return product;
      }

      if (quoteId > 0) {
        return {
          ...product,
          isSelected: !product.isSelected,
          productVariants: product.productVariants.map((variant) => {
            if (quotedVariantId === '') {
              return variant.isSelected
                ? {
                    ...variant,
                    isSelected: false
                  }
                : variant;
            }

            return {
              ...variant,
              isSelected: variant.variantId === quotedVariantId
            };
          })
        };
      }

      return {
        ...product,
        isSelected: !product.isSelected,
        policyStartDate: undefined,
        selectedTyreTypeId: undefined,
        selectedClaimLimit: undefined,
        selectedCoverLevelId: undefined,
        selectedPaymentMethod: undefined,
        productVariants: product.productVariants.map((variant) => {
          if (quotedVariantId === '') {
            return variant.isSelected
              ? {
                  ...variant,
                  isSelected: false,
                  price: product.isVariablePrice ? 0 : variant.price
                }
              : variant;
          }
          return {
            ...variant,
            isSelected: variant.variantId === quotedVariantId
          };
        })
      };
    });

    onUpdateProducts(updatedProducts, true);

    const selectedProductsUpdated = getSelectedProducts(updatedProducts);
    if (selectedProductsUpdated.length === 0) {
      onBack();
    }
  };

  const onChangePolicyStartDate = (productTypeId: number, dateVal: string) => {
    const updatedProducts = products.map((product) =>
      product.productTypeId === productTypeId
        ? { ...product, policyStartDate: dateVal }
        : product
    );

    onUpdateProducts(updatedProducts);
  };

  const onProductSelectionChange = (
    productTypeId: number,
    tyreTypeId: number | undefined,
    claimLimit: number | undefined,
    coverLevelId: number | undefined,
    excess: number | undefined,
    paymentMethod: string | undefined,
    variantId: string
  ) => {
    const updatedProducts = getUpdatedProducts(
      products,
      productTypeId,
      tyreTypeId,
      claimLimit,
      coverLevelId,
      excess,
      paymentMethod,
      variantId
    );

    onUpdateProducts(updatedProducts);
  };

  const getUpdatedProducts = (
    initialProducts: ProductType[],
    productTypeId: number,
    tyreTypeId: number | undefined,
    claimLimit: number | undefined,
    coverLevelId: number | undefined,
    excess: number | undefined,
    paymentMethod: string | undefined,
    variantId: string
  ) => {
    const updatedProducts = initialProducts.map((product) =>
      product.productTypeId === productTypeId
        ? {
            ...product,
            selectedTyreTypeId: tyreTypeId,
            selectedClaimLimit: claimLimit,
            selectedCoverLevelId: coverLevelId,
            selectedPaymentMethod: paymentMethod,
            selectedExcess: excess,
            productVariants: product.productVariants.map((variant) => ({
              ...variant,
              isSelected: variant.variantId === variantId
            }))
          }
        : product
    );

    return updatedProducts;
  };

  const onUpdateVariantPrice = (
    productTypeId: number,
    priceValue: number | undefined
  ) => {
    const updatedProducts = products.map((product) =>
      product.productTypeId === productTypeId
        ? {
            ...product,
            productVariants: product.productVariants.map((variant) =>
              variant.isSelected
                ? {
                    ...variant,
                    price: priceValue ?? 0
                  }
                : variant
            )
          }
        : product
    );

    onUpdateProducts(updatedProducts);
    setPriceInFocus(false);
  };

  const onBack = () => {
    navigate(PageRelativePath.Products);
    onPageChange(PageAbsolutePath.Products, false);
  };

  const onCheckout = () => {
    setValidationResults({});
    setStatus(ProductFormStatus.ValidatingCheckout);
  };

  const onSaveQuote = () => {
    setValidationResults({});
    setStatus(ProductFormStatus.ValidatingSaveQuote);
  };

  const onValidated = (productTypeId: number, isValid: boolean) => {
    setValidationResults((vr) => {
      return { ...vr, ...{ [productTypeId]: isValid } };
    });
  };

  const onPriceFocus = () => {
    setPriceInFocus(true);
  };

  const informationText = cms.information
    ? cms.information.replace('{NoOfInstalments}', noOfInstallments.toString())
    : '';

  const onCloseModal = () => {
    setShowModal(false);
  };

  const closeModalOnConfirm = () => {
    setShowModal(false);
    setDisableSaveQuote(true);
    setStatus(ProductFormStatus.Idle);
  };

  const onCloseVrnModal = () => {
    setShowVrnModal(false);
    setStatus(ProductFormStatus.Idle);
  };

  const onVrnUpdate = (inputVrn: string): void => {
    onUpdateVehicleVrn(inputVrn);
    onCloseVrnModal();
  };

  const getDefaultStartDate = (): string => {
    if (
      manufacturingCoverageMonths &&
      manufacturingCoverageMonths > 0 &&
      registrationDate
    ) {
      // Hardcoded culture because registration date is always in dd/MM/yyyy format
      const firstRegistrationDate = getDateByBrowserLanguage(
        'en-GB',
        registrationDate
      );

      if (!firstRegistrationDate) {
        return '';
      }

      const defaultStartDate = new Date(firstRegistrationDate);
      const month: number =
        defaultStartDate.getMonth() + (manufacturingCoverageMonths ?? 0);
      const day: number = defaultStartDate.getDate() + 1;

      defaultStartDate.setMonth(month);
      defaultStartDate.setDate(day);
      defaultStartDate.setHours(0, 0, 0, 0);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const isDateInPast = defaultStartDate < today;

      if (isDateInPast) {
        return '';
      }

      if (CommonValidation.isValidDate(defaultStartDate, false) === false) {
        return '';
      }

      const jsonDefaultStartDate = getJsonDateString(
        defaultStartDate.toISOString()
      );

      return jsonDefaultStartDate;
    }

    return '';
  };

  return (
    <div>
      <Helmet>
        <title>{t('page-title.customer-plan')}</title>
      </Helmet>

      <PageHeader
        title={cms.title}
        description={cms.description}
        information={informationText}
        warning={cms.warning}
        descriptionClass='p2'
      />
      <div className='flex-row'>
        <div className='flex-column product-selection-card-container'>
          {selectedProducts.map((product: ProductType) => (
            <ProductSelection
              key={`selection-${product.productTypeId}-${product.productSubTypeId}`}
              product={product}
              noOfInstallments={getSelectedNoOfInstallments(product)}
              paymentOptions={false}
              isQuote={quoteId > 0 ? true : false}
              addOrRemoveProduct={addOrRemoveProduct}
              onChangePolicyStartDate={onChangePolicyStartDate}
              onProductSelectionChange={onProductSelectionChange}
              onUpdateVariantPrice={onUpdateVariantPrice}
              onPriceFocus={onPriceFocus}
              vehicleDeliveryDate={vehicleDeliveryDate ?? ''}
              showPriceField={true}
              changeTotalPriceOnVariantChange={quoteId > 0}
              productFormStatus={status}
              onValidated={onValidated}
              culture={appConfig.cultureCode}
              hideRemovePlanLink={selectedProducts.length === 1}
              paymentProfileLabel={cms.paymentProfileLabel}
              manufacturersWarrantyRenewDate={
                product.productTypeId === ProductTypes.Warranty
                  ? getDefaultStartDate()
                  : ''
              }
              maxPolicyStartDateOffset={getMaxPolicyStartDateOffset(product)}
              selectedLinkedProducts={selectedLinkedProducts}
            />
          ))}
          <PriceTotal products={products} culture={appConfig.cultureCode} />
        </div>
      </div>
      <div className='flex-row form-nav navigation-desktop-hide text-align-center'>
        {appConfig.includeQuotes && (
          <ButtonContinue
            name='btn-save-products'
            text={quoteId ? saveChangesButtonText : saveQuoteButtonText}
            onClick={onSaveQuote}
            disabled={disableSaveQuote}
          />
        )}
      </div>
      <div className='flex-row form-nav'>
        <ButtonBack
          name='btnBack'
          onClick={onBack}
          text={t('customer-button.back-buttont-text')}
        />
        <div>
          {appConfig.includeQuotes && (
            <ButtonContinue
              name='btn-save-products'
              text={quoteId ? saveChangesButtonText : saveQuoteButtonText}
              onClick={onSaveQuote}
              disabled={disableSaveQuote}
              additionalClassNames='navigation-mobile-hide'
            />
          )}
          <SaveQuoteConfirmation
            showModal={showModal}
            onCloseModal={onCloseModal}
            closeModalOnConfirm={closeModalOnConfirm}
            products={products}
            vehicle={journey.vehicle}
            quoteId={journey.quoteId}
            personalDetails={journey.personalDetail}
            setPersonalDetails={setPersonalDetails}
            quoteType={QuoteType.Dealer}
            channels={ProductChannel.DealerJourney}
          />
          <ButtonContinue
            name='btn-see-products'
            text={t('customer-button.button-checkout-text')}
            onClick={onCheckout}
            disabled={priceInFocus}
            additionalClassNames='m05-left'
          />
        </div>
      </div>
      {showVrnModal && (
        <VrnInputModal
          vrn={vrn}
          onUpdateVrn={onVrnUpdate}
          showModal={showVrnModal}
          onCloseModal={onCloseVrnModal}
        />
      )}
    </div>
  );
};

export default CreatePlanPage;
